<script lang="ts">
  import CollapseSection from '$lib/components/common/CollapseSection.svelte';
  import type { FPost, IDetails, PostMedia, WithCreatorData } from 'garden-common';

  $: performanceTime = null;

  $: if (post.media && post.media.length > 0 && post.media[selectedMediaIndex]) {
    performanceTime = post?.media[selectedMediaIndex]?.details?.performanceTime;
  }

  $: findDate = (type: 'start' | 'end') => {
    if (!performanceTime) return null;

    const localize = (date: string | Date) =>
      new Date(date)?.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });

    if (performanceTime?.[type])
      return {
        date: localize(performanceTime?.[type].date),
        time: performanceTime?.[type].time
      };

    const fromOther = performanceTime.other.find((item: unknown) => {
      return item.hasOwnProperty(type);
    });

    if (fromOther) {
      return {
        date: localize(fromOther[type].date),
        time: fromOther[type].time
      };
    }

    return null;
  };

  const filterMediaStage = (input: any) => {
    const filteredData = Object.fromEntries(
      Object.entries(input).filter(([_, components]) =>
        (components as Array<any>).some((component: any) => {
          return Array.isArray(component.props.selectedValues) || component.props.value;
        })
      )
    );

    return filteredData;
  };

  const loadDetailSections = (media: PostMedia<IDetails<string>>): string[] => {
    const item = media?.details?.details;
    // Check if item is an object before calling Object.keys()
    if (!item || typeof item !== 'object') return [];

    return Object.keys(filterMediaStage(item));
  };

  export let post: Partial<FPost<string>> & Partial<WithCreatorData>;
  export let selectedMediaIndex: number;
  export let mediaDetails: IDetails<string> = null;
</script>

{#if post.wasItem && post?.classic?.data}
  <div class="flex flex-col gap-6 overflow-x-hidden">
    <CollapseSection>
      <div slot="title" class="font-medium text-blueish-grey-900">Classic</div>
      <p class="mt-1 text-xs">
        This is the posts original data from the classic garden, we recomend choosing a subcategory
        to best represent this data and describe your performance better with v1.5.0+
      </p>

      <div
        class="grid grid-flow-row grid-cols-2 gap-6 w-full border-t border-grey-transparency-dark border-opacity-5 pt-4 mt-4"
      >
        {#each Object.entries(post.classic.data) as [k, v]}
          {#if !['__v', '_id', 'item'].includes(k)}
            <div class="flex flex-col gap-2">
              <span class="text-blueish-grey-600 text-sm font-medium">{k}</span>
              <span class="text-garden-grey-800 text-sm font-medium">
                {#if typeof v === 'object'}
                  <div class="ml-2 flex flex-col">
                    {#each Object.entries(v) as [subk, subv]}
                      <span>
                        <span class="text-blueish-grey-600 text-sm font-medium">{subk}: </span>
                        <span class="text-garden-grey-800 text-sm font-medium">
                          {subv}
                        </span></span
                      >
                    {/each}
                  </div>
                {:else}
                  {JSON.stringify(v)}
                {/if}
              </span>
            </div>
          {/if}
        {/each}
      </div></CollapseSection
    >
  </div>
{/if}

<div class="flex flex-col gap-6 overflow-x-hidden">
  <CollapseSection contentOpen>
    <div slot="title" class="font-medium text-blueish-grey-900text-blueish-grey-900">
      Performance time
    </div>
    <div
      class="grid grid-flow-row grid-cols-2 gap-6 border-t border-grey-transparency-dark border-opacity-5 pt-4 mt-4"
    >
      <div class="flex flex-col gap-2 w-[800px]">
        {#if performanceTime?.totalHours}
          <div class="flex flex-col gap-2">
            <span class="text-blueish-grey-600 text-sm font-medium"> Total hours </span>
            <span class="text-garden-grey-800 text-sm font-medium">
              {performanceTime.totalHours}
            </span>
          </div>
        {/if}
        {#each performanceTime?.other || [] as otherField}
          {#if !otherField.start && !otherField.end}
            <div class="flex flex-col gap-2">
              <span class="text-blueish-grey-600 text-sm font-medium">
                {otherField.label}
              </span>
              <span class="text-garden-grey-800 text-sm font-medium">
                {otherField.value || '-'}
              </span>
            </div>
          {/if}
        {/each}
        {#if findDate('start')}
          {@const start = findDate('start')}
          <div class="flex gap-10">
            <div class="flex flex-col gap-1">
              <span class="text-blueish-grey-600 text-sm font-medium"> Start date </span>
              <span class="text-[#1D3932] text-sm font-medium">
                {start.date || '-'}
              </span>
            </div>
            <div class="flex flex-col gap-1">
              <span class="text-blueish-grey-600 text-sm font-medium"> Start time </span>
              <span class="text-garden-grey-800 text-sm font-medium">
                {start.time || '-'}
              </span>
            </div>
          </div>
        {/if}
        {#if findDate('end')}
          {@const end = findDate('end')}
          <div class="flex gap-10">
            <div class="flex flex-col gap-1">
              <span class="text-blueish-grey-600 text-sm font-medium"> End date </span>
              <span class="text-garden-grey-800 text-sm font-medium">
                {end.date || '-'}
              </span>
            </div>
            <div class="flex flex-col gap-1">
              <span class="text-blueish-grey-600 text-sm font-medium"> End time </span>
              <span class="text-garden-grey-800 text-sm font-medium">
                {end.time || '-'}
              </span>
            </div>
          </div>
        {/if}
      </div>
    </div>
  </CollapseSection>

  {#each loadDetailSections(post.media[selectedMediaIndex]) as subSection, idxs (idxs)}
    {#if subSection}
      <CollapseSection contentOpen>
        <div slot="title" class="font-medium text-blueish-grey-900">
          {subSection}
        </div>
        <div
          class="grid grid-flow-row grid-cols-2 gap-6 w-full border-t border-grey-transparency-dark border-opacity-5 pt-4 mt-4"
        >
          {#each mediaDetails[subSection] as field, idxf (idxf)}
            {#if !field.isExtra ? true : field.show}
              {#if field.props.selectedValues}
                <div class="flex flex-col">
                  <span class="text-blueish-grey-600 text-sm font-medium">
                    {field.props.label}
                  </span>
                  <span class="ml-1 text-garden-grey-800 text-xs font-medium">
                    {#each field.props.selectedValues as value}
                      {value || '-'},
                    {/each}
                  </span>
                </div>
              {/if}

              {#if field.props.value}
                <div class="flex flex-col gap-2">
                  <span class="text-blueish-grey-600 text-sm font-medium">
                    {field.props.label}
                  </span>
                  <span class="text-garden-grey-800 text-sm font-medium">
                    {field.props.value || '-'}
                  </span>
                </div>
              {/if}
            {/if}
          {/each}
        </div>
      </CollapseSection>
    {/if}
  {/each}
</div>
